import React, { useState, useEffect } from "react";
import { Modal, Select, DatePicker, Button, Spin, message } from "antd";
import { DownloadOutlined, CalendarOutlined } from "@ant-design/icons";
import ExportReportFormsModal from "./ExportReportFormsModal";
import {
  useGetCampaignsQuery,
  useGetFormQuestionsQuery,
  useGetFormsQuery,
  useGetOrganizationFormsQuery,
} from "../../store/api/apiSlice";
import axios from "axios";
import { useSelector } from "react-redux";
import { selectCurrentRole } from "../../store/auth/authSlice";

const ExportReportModal = ({ open, onClose, isNotModal }) => {
  const { RangePicker } = DatePicker;
  const [formsModalVisible, setFormsModalVisible] = useState(false);
  const token = useSelector((state) => state.auth.token);
  const userRole = useSelector(selectCurrentRole);
  // State for selected values
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [selectedQuestions, setSelectedQuestions] = useState([]);
  const [dateRange, setDateRange] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Get campaigns data
  const {
    data: campaignsData,
    isLoading: isLoadingCampaigns,
    error: campaignsError,
  } = useGetCampaignsQuery();

  // Get forms data - use different query based on user role
  const {
    data: adminFormsData,
    isLoading: isLoadingAdminForms,
    error: adminFormsError,
    refetch: refetchAdminForms,
  } = useGetFormsQuery(
    {},
    {
      skip: userRole !== "admin",
    }
  );

  const {
    data: orgFormsData,
    isLoading: isLoadingOrgForms,
    error: orgFormsError,
    refetch: refetchOrgForms,
  } = useGetOrganizationFormsQuery(
    {},
    {
      // Avoid preloading forms for interpreter role to stop /form/all on interpreter pages
      skip: userRole === "interpreter",
    }
  );


  const restructuredOrgFormsData = {
    data: orgFormsData?.data?.all_forms ?? orgFormsData?.data ?? [],
  };

  // Use the appropriate data based on user role
  const formsData =
    userRole === "admin" ? adminFormsData : restructuredOrgFormsData;
  const isLoadingForms =
    userRole === "admin" ? isLoadingAdminForms : isLoadingOrgForms;
  const formsError = userRole === "admin" ? adminFormsError : orgFormsError;
  const refetchForms =
    userRole === "admin" ? refetchAdminForms : refetchOrgForms;

  // Get questions for the selected form
  const {
    data: questionsData,
    isLoading: isLoadingQuestions,
    error: questionsError,
  } = useGetFormQuestionsQuery(selectedForm, {
    skip: !selectedForm,
  });

  // Reset form selection when campaign changes
  useEffect(() => {
    setSelectedForm(null);
    setSelectedQuestions([]);
  }, [selectedCampaign]);

  // Reset questions when form changes
  useEffect(() => {
    setSelectedQuestions([]);
  }, [selectedForm]);


  // Show error messages if API calls fail
  useEffect(() => {
    if (campaignsError) {
      message.error("Failed to load campaigns");
    }
    if (formsError) {
      console.error("Forms error:", formsError);
      message.error("Failed to load forms. Please try refreshing the page.");
    }
    if (questionsError) {
      message.error("Failed to load questions");
    }
  }, [campaignsError, formsError, questionsError]);

  // Handle form selection change
  const handleFormChange = (formId) => {
    setSelectedForm(formId);
  };

  // Handle individual question selection
  const handleQuestionSelection = (selectedQuestionIds) => {
    // Check if "all" is selected
    if (selectedQuestionIds.includes("all")) {
      // If "all" is selected, we should either add all questions or remove "all" based on context
      if (selectedQuestions.includes("all")) {
        // If "all" was already selected, the user is deselecting it
        const filteredIds = selectedQuestionIds.filter((id) => id !== "all");
        setSelectedQuestions(filteredIds);
      } else {
        // User is selecting "all", we'll add it to the selection
        setSelectedQuestions(["all"]);
      }
    } else {
      // Normal selection, no "all" involved
      setSelectedQuestions(selectedQuestionIds);
    }
  };

  // Handle date range selection
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // When the modal is closed, reset the form data and refetch reports
  const handleModalClose = () => {
    setFormsModalVisible(false);
    setReportData(null);
    refetchForms(); // Refetch the forms when the export modal is closed
  };

  // Prepare data for export and fetch the report data
  const handleExport = async () => {
    if (!selectedForm) {
      message.error("Please select a form before exporting");
      return;
    }

    setIsExporting(true);
    const params = new URLSearchParams();

    if (selectedCampaign) params.append("campaign", selectedCampaign);
    if (selectedForm) params.append("form", selectedForm);

    // If "all" is selected, add all question IDs to the payload
    if (selectedQuestions.includes("all")) {
      // Don't append any question ids - the backend will understand 'all'
      // params.append('question', 'all');
    } else {
      // Otherwise append the selected question IDs
      selectedQuestions.forEach((q, index) =>
        params.append(`question[${index}]`, q)
      );
    }

    if (dateRange && dateRange[0])
      params.append("start_date", dateRange[0].format("YYYY-MM-DD"));
    if (dateRange && dateRange[1])
      params.append("end_date", dateRange[1].format("YYYY-MM-DD"));

    try {
      // Call the API with Axios
      const response = await axios.get(
        `${
          import.meta.env.VITE_API_URL || process.env.VITE_API_URL
          // }/form/responses`,
        }/form/${selectedForm}/response/organization`,
        {
          params: params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data);
        setFormsModalVisible(true);
      } else {
        message.error(response.data.message || "Failed to load report data");
      }
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to generate report. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Get form options filtered by selected campaign and exclude template forms
  const getFormOptions = () => {
    if (!formsData?.data) {
      return [];
    }

    let forms = formsData.data;

    // Filter out template forms
    forms = forms.filter((form) => !form.is_template);

    // If a campaign is selected, filter forms by campaign
    if (selectedCampaign && campaignsData?.data) {
      const campaign = campaignsData.data.find(
        (c) => c._id === selectedCampaign
      );
      if (campaign && campaign.forms) {
        const campaignFormIds = campaign.forms.map((f) => f._id || f);
        forms = forms.filter((form) => campaignFormIds.includes(form._id));
      }
    }

    const options = forms.map((form) => ({
      value: form._id,
      label: form.title || form.name,
    }));

    return options;
  };

  const content = (
    <div className="space-y-6 py-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 block">
          Select Campaigns (Optional)
        </label>
        {isLoadingCampaigns ? (
          <div className="flex justify-center py-2">
            <Spin size="small" />
          </div>
        ) : (
          <Select
            className="w-full"
            placeholder="Select--"
            size="large"
            allowClear
            value={selectedCampaign}
            onChange={setSelectedCampaign}
          >
            {campaignsData?.data?.map((campaign) => (
              <Select.Option key={campaign._id} value={campaign._id}>
                {campaign.name || campaign.title}
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 block">
          Select Forms
        </label>
        {isLoadingForms ? (
          <div className="flex justify-center py-2">
            <Spin size="small" />
          </div>
        ) : (
          <Select
            className="w-full"
            placeholder={
              formsError
                ? "Error loading forms"
                : getFormOptions().length === 0
                ? "No forms available"
                : "Select--"
            }
            size="large"
            value={selectedForm}
            onChange={handleFormChange}
            notFoundContent={
              formsError ? (
                <div className="text-red-500">Failed to load forms</div>
              ) : getFormOptions().length === 0 ? (
                <div className="text-gray-500">No forms found</div>
              ) : (
                <div>No forms available</div>
              )
            }
            disabled={formsError || isLoadingForms}
          >
            {getFormOptions().map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 block">
          Share Responses of Questions
        </label>
        {isLoadingQuestions && selectedForm ? (
          <div className="flex justify-center py-2">
            <Spin size="small" />
          </div>
        ) : (
          <Select
            className="w-full"
            mode="multiple"
            placeholder={
              !selectedForm ? "Select a form first" : "Select questions"
            }
            size="large"
            value={selectedQuestions}
            onChange={handleQuestionSelection}
            disabled={!selectedForm}
            optionFilterProp="children"
          >
            <Select.Option key="all" value="all">
              All
            </Select.Option>
            {questionsData?.data?.map((question) => (
              <Select.Option key={question._id} value={question._id}>
                {question.title ||
                  question.text ||
                  `Question ${question.order || question.index || ""}`}
              </Select.Option>
            ))}
          </Select>
        )}
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 block">
          Duration of Responses (optional)
        </label>
        <RangePicker
          className="w-full h-11"
          size="large"
          suffixIcon={<CalendarOutlined className="text-gray-400" />}
          placeholder={["Select a date range--", ""]}
          style={{ borderRadius: "12px" }}
          value={dateRange}
          onChange={handleDateRangeChange}
          popupClassName="date-range-popup-mobile"
          getPopupContainer={() => document.body} // Changed to use document.body
          dropdownAlign={{
            points: ["tl", "bl"], // Align the top-left of popup with bottom-left of input
            offset: [0, 4],
            overflow: {
              adjustX: 1,
              adjustY: 1,
            },
          }}
        />
      </div>

      <Button
        type="primary"
        icon={<DownloadOutlined className="text-lg" />}
        className="w-full h-12 bg-[#18181B] hover:bg-[#18181B]/90 rounded-xl flex items-center justify-center gap-2 text-base font-medium"
        onClick={handleExport}
        disabled={!selectedForm || isExporting}
        loading={isExporting}
      >
        {isExporting ? "Generating Report..." : "Export Report"}
      </Button>

      {reportData && (
        <ExportReportFormsModal
          visible={formsModalVisible}
          onClose={handleModalClose}
          exportData={{
            reportData,
            campaign: selectedCampaign
              ? campaignsData?.data?.find((c) => c._id === selectedCampaign)
                  ?.name
              : "All Campaigns",
            form: selectedForm
              ? formsData?.data.find((f) => f._id === selectedForm)?.title
              : "",
            formObj: selectedForm
              ? formsData?.data.find((f) => f._id === selectedForm)
              : null,
            questionIds: selectedQuestions,
            dateRange: dateRange
              ? {
                  startDate: dateRange[0]?.format("YYYY-MM-DD"),
                  endDate: dateRange[1]?.format("YYYY-MM-DD"),
                }
              : null,
          }}
        />
      )}
    </div>
  );

  return isNotModal ? (
    content
  ) : (
    <Modal
      title={<div className="text-2xl font-semibold">Export Report</div>}
      open={open}
      onCancel={onClose}
      footer={null}
      width={500}
      className="rounded-xl"
      centered
    >
      {content}
    </Modal>
  );
};

export default ExportReportModal;
